import { reactive } from 'vue';

import apiClient from '@/services/api';

import { clearCoverLetterDetails } from '@/utils/coverLetterDetails';



const state = reactive({

  isLoggedIn: false,

  user: null,

  trialStatus: {

    isTrialActive: false,

    daysRemaining: 0,

    trialEndsAt: null,

    subscriptionEndDate: null,

    subscriptionDaysRemaining: 0

  }

});



const setAuth = (loggedIn, userData) => {

  state.isLoggedIn = loggedIn;

  state.user = userData;

  


  

  // Store user data in localStorage for persistence

  if (loggedIn && userData) {

    localStorage.setItem('user', JSON.stringify(userData));

    // Another account signed in on this browser: drop everyone else's cover letter answers.
    if (userData.id != null) clearCoverLetterDetails(userData.id);

  } else if (!loggedIn) {

    localStorage.removeItem('user');

    clearCoverLetterDetails();

  }

};



const checkAuthStatus = async () => {

  const token = localStorage.getItem('token');

  const userData = localStorage.getItem('user');

  

  if (token) {

    // Set the token in API client for future requests

    apiClient.defaults.headers.common['Authorization'] = `Token ${token}`;

    

    // Set a temporary login status until we can fetch the user data

    setAuth(true, userData ? JSON.parse(userData) : null);

    

    // Try to fetch user data to get complete user information

    try {

      const response = await apiClient.get('/auth/user/');

      let userData = response.data;

      // Also fetch jobseeker profile to get profile picture

      try {

        const jobseekerResponse = await apiClient.get('/jobseeker/profile/');


        if (jobseekerResponse.data && jobseekerResponse.data.profile_picture) {


          // Merge profile picture into user data

          userData = {

            ...userData,

            profile_picture: jobseekerResponse.data.profile_picture

          };


        }

      } catch (_) {
        // profile picture fetch failed, proceed without it
      }

      setAuth(true, userData);

    } catch (error) {

      console.error('Error fetching user data:', error);

      // Only a rejected token logs the user out. A network error or a 5xx while
      // the server restarts (a deploy) used to delete the token too, signing
      // everyone out (seen twice in the Oct 24 real-app test).
      const status = error?.response?.status;
      if (status === 401 || status === 403) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setAuth(false, null);
      } else {
        let cachedUser = null;
        try {
          cachedUser = userData ? JSON.parse(userData) : null;
        } catch (_) {
          cachedUser = null;
        }
        setAuth(true, cachedUser);
      }

    }

    

    return true;

  } else {

    setAuth(false, null);

    return false;

  }

};



const markEmailVerified = () => {

  if (state.user) {

    state.user = { ...state.user, email_verified: true };

    localStorage.setItem('user', JSON.stringify(state.user));

  }

};



const logout = () => {

  state.isLoggedIn = false;

  state.user = null;

  localStorage.removeItem('token');

  localStorage.removeItem('user');

  clearCoverLetterDetails();

};



const checkSubscriptionStatus = async () => {

  try {

    // Get both trial status and user data to ensure we have the latest subscription info

    const [trialResponse, userResponse] = await Promise.all([

      apiClient.get('/auth/trial/status/'),

      apiClient.get('/auth/user/')

    ]);

    

    const subEndDate = trialResponse.data.subscription?.end_date || null;
    const subscriptionDaysRemaining = subEndDate
      ? Math.max(0, Math.ceil((new Date(subEndDate) - new Date()) / (1000 * 60 * 60 * 24)))
      : 0;

    state.trialStatus = {

      isTrialActive: trialResponse.data.is_trial_active,

      daysRemaining: trialResponse.data.days_remaining || subscriptionDaysRemaining,

      trialEndsAt: trialResponse.data.trial_ends_at || subEndDate,

      subscriptionEndDate: subEndDate,

      subscriptionDaysRemaining

    };

    

    // Update user with latest subscription info

    state.user = userResponse.data;

    

    // Update localStorage with latest user data

    localStorage.setItem('user', JSON.stringify(userResponse.data));

    

    return {

      isTrialActive: trialResponse.data.is_trial_active,

      daysRemaining: trialResponse.data.days_remaining,

      trialEndsAt: trialResponse.data.trial_ends_at,

      hasActiveSubscription: userResponse.data.has_active_subscription,

      subscription: userResponse.data.subscription

    };

  } catch (error) {

    console.error('Error fetching subscription status:', error);

    return {

      isTrialActive: false,

      daysRemaining: 0,

      trialEndsAt: null,

      hasActiveSubscription: false,

      subscription: null

    };

  }

};



export default {

  state,

  setAuth,

  checkAuthStatus,

  markEmailVerified,

  logout,

  checkSubscriptionStatus,

  // Keep checkTrialStatus for backward compatibility, but make it call checkSubscriptionStatus

  checkTrialStatus: checkSubscriptionStatus

};

